/**
 * Global keyboard-shortcut layer + `?` cheat-sheet overlay.
 *
 * A single window-level listener (mounted once in the shell) that adds a
 * discoverable shortcut surface on top of the existing Cmd/Ctrl-K palette:
 *
 *   ?            open this cheat-sheet
 *   /            open the command palette (global search)
 *   g then <key> "go to" navigation (g d = dashboard, g c = contenu, …)
 *
 * Guards: never fires while the user is typing in an input/textarea/select or
 * a contenteditable, never with a modifier held (so Cmd/Ctrl-K etc. keep
 * working), and never while a modal/palette is open (`[aria-modal="true"]`).
 * `g`-chords expire after 1.2 s.
 *
 * The overlay reuses the shared `@/ui` `Modal` (focus-trap, scroll-lock,
 * Escape/backdrop close, bottom-sheet on mobile), so it is theme-aware and
 * accessible for free.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { Modal } from '@/ui';

/** "Go to" chords: the key pressed after `g`, and where it navigates. */
export const GOTO_NAV: { key: string; to: string; label: string }[] = [
  { key: 'd', to: '/', label: 'Tableau de bord' },
  { key: 'c', to: '/content', label: 'Contenu' },
  { key: 'k', to: '/calendar', label: 'Calendrier' },
  { key: 'p', to: '/publishing', label: 'Publication' },
  { key: 'l', to: '/platforms', label: 'Plateformes' },
  { key: 'm', to: '/media-library', label: 'Médiathèque' },
  { key: 'a', to: '/analytics', label: 'Analytics' },
  { key: 'w', to: '/workflows', label: 'Workflows' },
  { key: 'r', to: '/crm', label: 'CRM & Leads' },
  { key: 'n', to: '/social', label: 'Leads sociaux' },
  { key: 'o', to: '/outreach', label: 'Email Outreach' },
  { key: 'i', to: '/inbox', label: 'Inbox sociale' },
  { key: 'e', to: '/listening', label: 'Écoute sociale' },
  { key: 'b', to: '/bio', label: 'Link-in-bio' },
  { key: 'j', to: '/projects', label: 'Projets' },
  { key: 'x', to: '/ai-activity', label: 'Activité IA' },
  { key: 's', to: '/settings', label: 'Paramètres' },
];

const GOTO_BY_KEY = new Map(GOTO_NAV.map((g) => [g.key, g] as const));

/** Returns true when the event target is a text-entry surface. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable === true
  );
}

/** A modal/dialog/palette is currently open (so global chords should defer). */
function overlayOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') != null;
}

export interface GlobalShortcutHandlers {
  /** Open the `?` cheat-sheet overlay. */
  onHelp: () => void;
  /** Open the command palette (mapped to `/`). */
  onOpenPalette: () => void;
}

/**
 * Registers the global shortcut listener for the lifetime of the shell.
 * Handlers are read through a ref so the listener stays stable.
 */
export function useGlobalShortcuts({ onHelp, onOpenPalette }: GlobalShortcutHandlers): void {
  const navigate = useNavigate();
  const handlers = useRef({ onHelp, onOpenPalette });
  handlers.current = { onHelp, onOpenPalette };
  // Timestamp of the last standalone `g` press (0 = no pending chord).
  const pendingG = useRef(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      if (overlayOpen()) return;

      const key = e.key;

      // `?` (Shift + / on most layouts) → cheat-sheet.
      if (key === '?') {
        e.preventDefault();
        pendingG.current = 0;
        handlers.current.onHelp();
        return;
      }

      // `/` → open the command palette as a global search entry point.
      if (key === '/') {
        e.preventDefault();
        pendingG.current = 0;
        handlers.current.onOpenPalette();
        return;
      }

      const lower = key.toLowerCase();

      // Second key of a `g …` chord.
      if (pendingG.current && Date.now() - pendingG.current < 1200) {
        pendingG.current = 0;
        const target = GOTO_BY_KEY.get(lower);
        if (target) {
          e.preventDefault();
          void navigate(target.to);
        }
        return;
      }

      // Start a `g …` chord.
      if (lower === 'g') {
        pendingG.current = Date.now();
        return;
      }

      pendingG.current = 0;
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
}

// ---------------------------------------------------------------------------
// Cheat-sheet overlay
// ---------------------------------------------------------------------------

function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {keys.map((k, i) => (
        <kbd
          key={`${k}-${i}`}
          className="min-w-[1.5rem] rounded border border-border bg-surface-2 px-1.5 py-0.5 text-center text-[11px] font-medium text-foreground"
        >
          {k}
        </kbd>
      ))}
    </span>
  );
}

function Row({ keys, label }: { keys: string[]; label: ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-token px-2 py-1.5 text-sm text-foreground">
      <span className="min-w-0 truncate">{label}</span>
      <Keys keys={keys} />
    </li>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
        {title}
      </h3>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

const MOD_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  ? '⌘'
  : 'Ctrl';

export interface ShortcutsOverlayProps {
  open: boolean;
  onClose: () => void;
}

/** The `?` cheat-sheet listing every global shortcut, grouped. */
export function ShortcutsOverlay({ open, onClose }: ShortcutsOverlayProps) {
  if (!open) return null;
  return (
    <Modal title="Raccourcis clavier" size="lg" onClose={onClose}>
      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
        <Section title="Général">
          <Row keys={[MOD_KEY, 'K']} label="Palette de commandes" />
          <Row keys={['/']} label="Rechercher" />
          <Row keys={['?']} label="Afficher cette aide" />
          <Row keys={['Esc']} label="Fermer / revenir en arrière" />
        </Section>

        <Section title="Navigation « g » puis…">
          {GOTO_NAV.slice(0, Math.ceil(GOTO_NAV.length / 2)).map((g) => (
            <Row key={g.key} keys={['g', g.key]} label={g.label} />
          ))}
        </Section>

        <Section title="Navigation (suite)">
          {GOTO_NAV.slice(Math.ceil(GOTO_NAV.length / 2)).map((g) => (
            <Row key={g.key} keys={['g', g.key]} label={g.label} />
          ))}
        </Section>

        <Section title="Dans la palette">
          <Row keys={['↑', '↓']} label="Parcourir les résultats" />
          <Row keys={['↵']} label="Ouvrir la sélection" />
        </Section>
      </div>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted">
        Astuce : appuyez sur <kbd className="rounded border border-border bg-surface-2 px-1 text-[10px]">g</kbd>{' '}
        puis une lettre pour changer de section sans quitter le clavier.
      </p>
    </Modal>
  );
}
